import Time from "./Time";

const MAX_AGE_MS = 300000;
const SYNC_INTERVAL_MS = 120000;
const MAX_RTT_MS = 5000;
const PAUSE_MS = 5000;
const RETRY_DELAYS = [5000, 15000, 30000];

export const createServerClock = ({
  request = Time.getServerTime,
  monotonicNow = () => performance.now(),
  wallNow = () => Date.now(),
} = {}) => {
  const listeners = new Set();
  let anchor = null;
  let pending = null;
  let generation = 0;
  let retryTimer = null;
  let failures = 0;
  let snapshot = { nowMs: null, status: "idle", syncVersion: 0, errorReason: null };

  const isVisible = () => document.visibilityState !== "hidden" && navigator.onLine !== false;
  const getNowMs = () => {
    if (!anchor) return null;
    const elapsed = monotonicNow() - anchor.monotonicMs;
    // Local time only detects a clock change or sleep; it never supplies server time.
    const interrupted = Math.abs(wallNow() - anchor.wallMs - elapsed) > PAUSE_MS;
    return elapsed >= 0 && elapsed <= MAX_AGE_MS && !interrupted ? anchor.serverMs + elapsed : null;
  };
  const publish = (status = snapshot.status, synced = false, errorReason = snapshot.errorReason) => {
    snapshot = { nowMs: getNowMs(), status, syncVersion: snapshot.syncVersion + Number(synced), errorReason };
    listeners.forEach((listener) => listener());
  };
  const cancelSync = () => {
    generation += 1;
    pending?.controller.abort();
    pending = null;
    clearTimeout(retryTimer);
    retryTimer = null;
  };
  const invalidate = () => {
    cancelSync();
    anchor = null;
    publish("stale", false, null);
  };
  const sync = ({ invalidate: shouldInvalidate = false } = {}) => {
    if (shouldInvalidate) invalidate();
    if (pending) return pending.promise;
    if (!isVisible()) {
      if (navigator.onLine === false) publish(getNowMs() == null ? "error" : "ready", false, "network");
      return Promise.resolve();
    }
    clearTimeout(retryTimer);
    retryTimer = null;
    const controller = new AbortController();
    const requestGeneration = generation;
    const startedAt = monotonicNow();
    const current = { controller, promise: null };
    pending = current;
    if (getNowMs() == null) publish("syncing", false, null);
    current.promise = (async () => {
      let errorReason = "network";
      try {
        const data = await request({ signal: controller.signal });
        if (generation !== requestGeneration) return;
        const unixMs = data?.unixMs;
        const receivedAt = monotonicNow();
        const rtt = receivedAt - startedAt;
        if (
          !Number.isSafeInteger(unixMs) ||
          unixMs <= 0 ||
          unixMs > 8640000000000000 ||
          rtt < 0
        ) {
          errorReason = "invalid-response";
          throw new Error("Invalid server time sample");
        }
        if (rtt > MAX_RTT_MS) {
          errorReason = "slow-response";
          throw new Error("Server time sample took too long");
        }
        // Half the round-trip is an estimate of delivery delay, not an exact correction.
        anchor = { serverMs: unixMs + rtt / 2, monotonicMs: receivedAt, wallMs: wallNow() };
        failures = 0;
        publish("ready", true, null);
      } catch (error) {
        if (generation !== requestGeneration) return;
        if (error?.code === "ECONNABORTED" || error?.code === "ETIMEDOUT") errorReason = "timeout";
        publish(getNowMs() == null ? "error" : "ready", false, errorReason);
        if (listeners.size && isVisible()) {
          const delay = RETRY_DELAYS[Math.min(failures++, RETRY_DELAYS.length - 1)];
          retryTimer = setTimeout(() => {
            retryTimer = null;
            sync();
          }, delay);
        }
      } finally {
        if (pending === current) pending = null;
      }
    })();
    return current.promise;
  };
  const ensureTime = async () => {
    if (getNowMs() == null || monotonicNow() - anchor.monotonicMs >= SYNC_INTERVAL_MS) await sync();
    return getNowMs();
  };
  const resumeInitialCheck = () => {
    // Recover an unfinished initial check, never refresh an already displayed view.
    if (snapshot.nowMs == null && isVisible()) sync();
  };
  const subscribe = (listener) => {
    listeners.add(listener);
    if (listeners.size === 1) {
      window.addEventListener("online", resumeInitialCheck);
      document.addEventListener("visibilitychange", resumeInitialCheck);
      publish(getNowMs() != null ? "ready" : "stale");
      ensureTime();
    }
    return () => {
      listeners.delete(listener);
      if (listeners.size) return;
      window.removeEventListener("online", resumeInitialCheck);
      document.removeEventListener("visibilitychange", resumeInitialCheck);
      cancelSync();
      // Preserve the anchor across list/tab remounts, but never expose an old
      // render snapshot before the next subscription has revalidated it.
      snapshot = { nowMs: null, status: "idle", syncVersion: snapshot.syncVersion, errorReason: null };
    };
  };
  return { subscribe, getSnapshot: () => snapshot, getNowMs, sync, ensureTime };
};

export default createServerClock();
